import React, { useMemo } from "react";
import { Box, Divider, Typography } from "@mui/material";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import StatTile from "./StatTile";
import SpendByTypeChart from "./SpendByTypeChart";
import SpendOverTimeChart from "./SpendOverTimeChart";

// Scoped to the selected car, computed client-side from records App.js
// already holds — no new backend endpoint. All four metrics are inherently
// per-vehicle (pooling cost-per-mile across cars of different ages/mileage
// would be less meaningful, not more), matching how Maintenance/Reminders
// already treat "selected car" as the unit of context.
function InsightsDashboard({ records = [], selectedCar, currentMileage = 0 }) {
  const stats = useMemo(() => {
    const costRecords = records.filter((record) => record.cost != null);
    const totalSpend = costRecords.reduce((sum, record) => sum + Number(record.cost || 0), 0);
    const costPerMile = currentMileage > 0 ? totalSpend / currentMileage : null;

    const byType = new Map();
    costRecords.forEach((record) => {
      const key = record.service_type || record.serviceType || "Other";
      byType.set(key, (byType.get(key) || 0) + Number(record.cost || 0));
    });
    const spendByType = Array.from(byType.entries())
      .map(([service_type, total]) => ({ service_type, total }))
      .sort((a, b) => b.total - a.total);

    const byMonth = new Map();
    costRecords.forEach((record) => {
      const month = String(record.date || "").slice(0, 7);
      if (!month) return;
      byMonth.set(month, (byMonth.get(month) || 0) + Number(record.cost || 0));
    });
    const spendOverTime = Array.from(byMonth.entries())
      .map(([month, total]) => ({ month, total }))
      .sort((a, b) => a.month.localeCompare(b.month));

    return {
      totalSpend,
      costPerMile,
      spendByType,
      spendOverTime,
      hasCostData: costRecords.length > 0,
    };
  }, [records, currentMileage]);

  if (!selectedCar) {
    return (
      <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Select a car to view cost insights</Typography>
      </Box>
    );
  }

  if (records.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>No maintenance records yet</Typography>
        <Typography variant="body2">Log services with a cost to start seeing insights here.</Typography>
      </Box>
    );
  }

  if (!stats.hasCostData) {
    return (
      <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>No cost data yet</Typography>
        <Typography variant="body2">
          Add a cost when logging maintenance to unlock spend insights for this car.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 3 }}>
        <StatTile
          label="Total Spend"
          value={`$${stats.totalSpend.toFixed(2)}`}
          icon={<PaidRoundedIcon fontSize="small" />}
          accent
        />
        <StatTile
          label="Cost per Mile"
          value={stats.costPerMile != null ? `$${stats.costPerMile.toFixed(3)}` : "—"}
          icon={<SpeedRoundedIcon fontSize="small" />}
        />
      </Box>

      <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Spend by Service Type</Typography>
      <SpendByTypeChart data={stats.spendByType} />

      <Divider sx={{ my: 3.5 }} />

      <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Spend Over Time</Typography>
      <SpendOverTimeChart data={stats.spendOverTime} />
    </Box>
  );
}

export default InsightsDashboard;
